import { useState, useEffect, forwardRef, useRef } from "react";
import parse from "html-react-parser";
import { CommentEditor, Editor, UserAvatar } from "./index";
import { SmallSpinner } from "@/react/components/Spinner";
import AttachmentThumbnails from "./Attachments";
import { getFormattedTime, getFormattedDay, getFormattedDate, highlightElement } from "../utils";
import { CommentContextProvider, useCommentContext, useScopedTopicsInstance, useThreadContext, useTopicsDataStore, useTopicsPanelProvier, useTopicsStoreProvider } from "../Context";
import { useDeleteComment, useFetchReplies, useOptimisticComment, useUpdateComment } from "../hooks";
import Swal from '@/react/components/Swal'

const ViewReplies = ({ isRepliesOpen, fetchReplies, isLoading }) => {
	const { replyCount } = useCommentContext();
	return <div className={`flex relative items-center hover:cursor-pointer group text-xs`} onClick={fetchReplies}>
		<div className="marker !-top-[3px]"></div>
		<span className="bg-white cursor-pointer text-grey-text group-hover:underline">
			{`${isRepliesOpen ? 'Hide' : 'View'}`} {!isRepliesOpen ? (`${replyCount} ${replyCount === 1 ? "reply" : "replies"}`) : 'replies'}
		</span>
		{isLoading && <SmallSpinner className="mt-2 ml-2" />}
	</div>
}

const DayDivider = ({ day }) => {
	return <div className={`w-full flex items-center text-grey-text text-xs font-medium bg-white`}>
		<hr className="flex-1" />
		<span className="rounded-full px-4 py-1 bg-white border-normal border-grey">{day}</span>
		<div className="flex-1 flex items-center gap-1 grow-[1]">
			<hr className="grow" />
		</div>
	</div>
}

const Comment = forwardRef(({ isNested = false, id, previousCommentId = null }, ref) => {
	const topicsInstance = useScopedTopicsInstance();
	const { currentUser } = topicsInstance;
	const selfRef = useRef(null);

	const { id: threadId } = useThreadContext();

	const comment = useTopicsDataStore(state => state.comments.get(id));
	const prevComment = useTopicsDataStore(state => state.comments.get(previousCommentId));

	const { user, isDeleted, replyCount, immediateRepliesCount } = comment;

	// Check if current user can edit/delete this comment
	const isOwner = currentUser.id === user.id;
	const canEdit = isOwner && !isDeleted;
	const canDelete = isOwner && !isDeleted;

	const replyIds = useTopicsStoreProvider(s => s.mapCommentToReplies.get(id)) || new Set();
	const surfacedCommentIds = useTopicsStoreProvider(s => s.mapSurfacedCommentIds.get(id)) || new Set();

	// hooks
	const { data: repliesData, isLoading: isRepliesLoading, refetch: fetchReplies, dataUpdatedAt } = useFetchReplies(id);
	const { mutateAsync: editCommentMutation, isPending: editCommentPending } = useUpdateComment({
		id,
		threadId,
	});
	const { mutateAsync: deleteCommentMutation, isPending: deleteCommentPending } = useDeleteComment({ id, threadId });

	const { optimisticList: optimisticReplies, create: handleCreateReply, retry: handleRetry } =
		useOptimisticComment({
			threadId,
			parentCommentId: id,
			onSuccessStoreFn: (real) => topicsInstance.postReplyToComment(id, real)
		});

	useEffect(() => {
		if (repliesData && repliesData.length > 0) {
			topicsInstance.setRepliesToComment(id, repliesData);
		}
	}, [repliesData, dataUpdatedAt])

	const handleFetchReplies = async () => {
		setShowEditor(true);
		// fetch replies logic
		!isRepliesOpen && await fetchReplies();

		isRepliesOpen && topicsInstance.hideRepliesFromComment(id);
	}

	const handleEditComment = async (updatedComment) => {
		await editCommentMutation(updatedComment);
		setIsEditing(false);
		highlightElement(selfRef.current);
	}

	const handleDeleteComment = async () => {
		const result = await Swal.fire({
			title: `Are you sure?`,
			text: "Are you sure you want to delete this comment? This cannot be undone.",
			icon: 'delete',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Delete'
		})

		result.isConfirmed && await deleteCommentMutation();
	}

	if (!comment) {
		return null;
	}

	const [showEditor, setShowEditor] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	const isRepliesOpen = replyIds.size > 0;

	const hasChildren = replyCount > 0 || showEditor

	const showViewReplies = replyCount > 0 && immediateRepliesCount !== surfacedCommentIds.size;

	const currentDay = comment.createdAt
		? getFormattedDay(comment.createdAt)
		: null;

	const previousDay = prevComment?.createdAt
		? getFormattedDay(prevComment.createdAt)
		: null;

	const showDayDivider = previousCommentId === null ? false : currentDay && currentDay !== previousDay;

	return (
		<CommentContextProvider comment={{ ...comment, isEditing }}>
			{
				showDayDivider && <DayDivider day={getFormattedDate(comment.createdAt)} />
			}
			<div ref={selfRef} data-comment-id={id} className={`relative flex flex-col`}>
				<div className="flex gap-1">
					{isNested && <div className={`marker h-7`}></div>}
					<div className="shrink-0 flex flex-col items-center relative z-50 pt-1" data-lexical-mention-id={user.id}>
						<UserAvatar user={user} />
					</div>

					<div className="flex flex-col text-article text-black grow">
						{/* this div basically shows the vertical line to the left */}
						<div className={`relative flex flex-col gap-2 group p-2 py-1.5 ${!isEditing && !isDeleted ? 'hover:bg-grey/15' : ''} rounded-primary transition-all ${isEditing ? 'bg-primary/15' : ''} ${editCommentPending || deleteCommentPending ? 'thread-comment-loading' : ''}`}>
							{/* check the vertical line here */}
							{hasChildren && <VerticalLine />}

							<CommentHeader />

							{
								isEditing ? (
									<Editor
										placeholder={`Edit your comment`}
										defaultValue={comment.content}
										commentEdit={true}
										focus={true}
										onSubmit={handleEditComment}
										clearSubmission={false}
										editable={!editCommentPending}
										onCancel={() => setIsEditing(false)}
									/>
								) : <CommentContent />
							}

							<CommentAttachments />

							<CommentActions setShowEditor={setShowEditor} />

							{/* Only show edit/delete buttons if user is owner and comment is not deleted */}
							{
								canEdit && !isEditing && (
									<div className="absolute top-0 right-0 group-hover:opacity-100 opacity-0 transition-all flex items-center gap-2 rounded-primary px-1.5 bg-white border-normal border-grey">
										<button
											onClick={() => setIsEditing(true)}
											className="py-1 text-base btn btn-icon animation-none animation-pop-out font-normal bg-transparent hover:text-primary text-grey-text fk-icons"
										>
											edit
										</button>
										{canDelete && (
											<>
												<div className="w-[2px] self-stretch bg-grey"></div>
												<button
													className="text-base py-1 btn btn-icon animation-none animation-pop-out font-normal bg-transparent hover:text-secondary text-grey-text fk-icons"
													onClick={handleDeleteComment}
												>
													delete
												</button>
											</>
										)}
									</div>
								)
							}
						</div>
						<div className="comments-markers-container">
							{
								// this is for replies
								Array.from(replyIds).map((id) => {
									return <Comment
										key={id}
										id={id}
										isNested={true}
									/>
								})
							}

							{
								showViewReplies && <ViewReplies isRepliesOpen={isRepliesOpen} fetchReplies={handleFetchReplies} isLoading={isRepliesLoading} />
							}

							{
								// this is for surfaced comments
								!isRepliesOpen && Array.from(surfacedCommentIds).map((id) => (
									<Comment
										key={id}
										id={id}
										isNested={true}
									/>
								))
							}

							{
								optimisticReplies.map((optimisticComment) => (
									<OptimisticComment
										optimisticComment={optimisticComment}
										key={optimisticComment.id}
										isNested={true}
										onRetry={handleRetry}
									/>
								))
							}

							{
								showEditor && (
									<div className="relative pt-4 editor">
										<div className="marker !top-[14px]"></div>
										<CommentEditor
											user={currentUser}
											editorProps={
												{
													placeholder: `Reply to ${user.full_name}`,
													onSubmit: handleCreateReply,
													focus: true
												}
											}
										/>
									</div>
								)
							}
						</div>
						{/* end */}
					</div>
				</div>
			</div>
		</CommentContextProvider>
	)

})


export const OptimisticComment = ({ isNested = false, optimisticComment, onRetry }) => {
	const { isPending, isFailed } = optimisticComment;
	const ref = useRef(null);

	useEffect(() => {
		if (isPending && ref.current) {
			// scroll into view so that the bottom of the comment is visible
			ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [isPending])

	return <CommentContextProvider comment={optimisticComment}>
		<div className={`relative flex flex-col ${isPending && 'thread-comment-loading'}`} ref={ref}>
			<div className="flex gap-2">
				<div className="shrink-0 flex flex-col items-center relative">
					{isNested && <div className={`marker !-top-[3px]`}></div>}
					<UserAvatar user={optimisticComment.user} />
				</div>

				<div className="flex flex-col text-article text-black mt-1.5 grow">
					{/* this div basically shows the vertical line to the left */}
					<div className="relative flex flex-col gap-2 group hover:bg-grey/10 rounded-primary transition-all">
						<OptimisticCommentHeader />
						<CommentContent />
						<CommentAttachments />
						{!isFailed && <div className="h-4"></div>}
						{isFailed && <div className="flex items-center gap-1 text-primary hover:cursor-pointer" onClick={() => onRetry(optimisticComment)}>Retry <span className="font-MaterialIconsNew text-lg align-middle">refresh</span> </div>}
					</div>
					{/* end */}
				</div>
			</div>
		</div>
	</CommentContextProvider>
}

const VerticalLine = ({ left = -26, className = "" }) => <div className={"absolute w-[1px] top-0 h-full bg-grey rounded-full -z-10 mt-1" + className}
	style={{ left: left + 'px' }}
/>

const OptimisticCommentHeader = () => {
	const { user, isPending, isFailed } = useCommentContext();

	return (<div className="flex items-center gap-2">
		<span className="text-placeholder font-semibold whitespace-nowrap">{user.full_name}</span>
		<div className="text-grey-text text-xs font-thin mt-[1px]">
			{isPending && <span className="text-primary">Posting...</span>}
			{isFailed && <span className="text-secondary">Failed to post</span>}
		</div>
	</div>
	)
}

const CommentHeader = () => {
	const { createdAt, user, isEdited, isDeleted } = useCommentContext();

	const time = createdAt && getFormattedTime(createdAt) || 'Just now';

	return <div className="flex items-center gap-2">
		<span className="text-placeholder font-semibold whitespace-nowrap" data-lexical-mention-id={user.id}>{user.full_name}</span>
		<div className="text-grey-text text-xs font-thin mt-[1px]">
			<span>{time}</span>
		</div>
		{isEdited && !isDeleted && <span className="px-2 py-1 bg-grey/20 text-black text-xs rounded-primary">Edited</span>}
	</div>
}

const CommentContent = () => {
	const { content, isDeleted } = useCommentContext();
	const context = useTopicsPanelProvier();

	if (isDeleted) {
		return <div className="p-2 px-4 rounded-primary bg-grey/20 text-black italic shrink self-start">This comment has been deleted</div>
	}

	if (!content) {
		return null;
	}

	const searchTerm = context?.searchTerm || '';
	const parsedContent = searchTerm ? parse(content.replace(new RegExp(`(${searchTerm})`, 'gi'), '<mark>$1</mark>')) : parse(content);

	return (
		<div className="lexical-editor-threads">
			{parsedContent}
		</div>
	);
}

const CommentAttachments = () => {
	const { attachments } = useCommentContext();
	if (!attachments || attachments.length === 0) return null;
	return <div className="mt-2">
		<AttachmentThumbnails attachments={attachments} />
	</div>
}

const CommentActions = ({ setShowEditor }) => {
	const { isResolved } = useThreadContext();
	const { isDeleted, isEditing } = useCommentContext();

	return <div className="flex items-center gap-4 text-grey-text">
		{/* <div className="flex items-center gap-1 text-xs">
			<span className="font-MaterialIconsNew text-base cursor-pointer hover:text-primary transition-all align-middle">thumb_up</span>
			<span>{likes_count}</span>
		</div> */}
		{
			!isResolved && !isDeleted && !isEditing ?
				<span className="cursor-pointer hover:underline transition-all text-xs" onClick={() => {
					setShowEditor(true);
				}}>Reply</span>
				: null
		}
	</div>

}

export default Comment;